import React, { useEffect, useState } from 'react';
import { Calendar, Clock, Globe, Loader2, Moon, Palette, Save, Sun } from 'lucide-react';
import { Dropdown } from '../../components/forms';
import {
  useGetPreferencesQuery,
  useUpdatePreferencesMutation,
  UserPreferences,
} from '../../services/profileApi';
import { useToast } from '../../contexts/ToastContext';

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Espa\u00f1ol (Spanish)' },
  { value: 'fr', label: 'Fran\u00e7ais (French)' },
];

const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
];

const DATE_FORMAT_OPTIONS = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2024)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2024)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2024-12-31)' },
  { value: 'DD MMM YYYY', label: 'DD MMM YYYY (31 Dec 2024)' },
  { value: 'MMM DD, YYYY', label: 'MMM DD, YYYY (Dec 31, 2024)' },
];

const TIME_FORMAT_OPTIONS = [
  { value: '12h', label: '12-hour (2:30 PM)' },
  { value: '24h', label: '24-hour (14:30)' },
];

const THEME_OPTIONS = [
  { value: 'light', label: 'Light', icon: Sun, description: 'Always use light mode' },
  { value: 'dark', label: 'Dark', icon: Moon, description: 'Always use dark mode' },
  { value: 'system', label: 'System', icon: Palette, description: 'Match your system settings' },
];

const ProfilePreferences: React.FC = () => {
  const { success, error: showError } = useToast();
  const { data: preferences, isLoading, error } = useGetPreferencesQuery();
  const [updatePreferences, { isLoading: isSaving }] = useUpdatePreferencesMutation();

  const [localPrefs, setLocalPrefs] = useState<Partial<UserPreferences>>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize local state when preferences load
  useEffect(() => {
    if (preferences) {
      setLocalPrefs({
        language: preferences.language,
        timezone: preferences.timezone,
        dateFormat: preferences.dateFormat,
        timeFormat: preferences.timeFormat,
        theme: preferences.theme,
      });
    }
  }, [preferences]);

  const handleChange = (key: keyof UserPreferences, value: string) => {
    setLocalPrefs(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      await updatePreferences(localPrefs).unwrap();
      success('Preferences saved successfully');
      setHasChanges(false);
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to save preferences');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">
          Failed to load preferences. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 max-w-4xl">
      {/* Page Header */}
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-gray-900">Preferences</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Customize your experience with language, timezone, and display settings
        </p>
      </div>

      {/* Language & Region */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 mb-4">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-9 h-9 bg-brand-50 rounded-lg flex items-center justify-center">
            <Globe className="w-4 h-4 text-brand-primary" />
          </div>
          <div>
            <h2 className="text-sm font-medium text-gray-900">Language & Region</h2>
            <p className="text-xs text-gray-500">Set your preferred language and timezone</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Dropdown
            label="Language"
            options={LANGUAGE_OPTIONS}
            value={localPrefs.language || 'en'}
            onChange={value => handleChange('language', value as string)}
            placeholder="Select language"
          />

          <Dropdown
            label="Timezone"
            options={TIMEZONE_OPTIONS}
            value={localPrefs.timezone || 'America/New_York'}
            onChange={value => handleChange('timezone', value as string)}
            placeholder="Select timezone"
          />
        </div>
      </div>

      {/* Date & Time */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 mb-4">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-9 h-9 bg-brand-50 rounded-lg flex items-center justify-center">
            <Calendar className="w-4 h-4 text-brand-primary" />
          </div>
          <div>
            <h2 className="text-sm font-medium text-gray-900">Date & Time Format</h2>
            <p className="text-xs text-gray-500">Choose how dates and times are displayed</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Dropdown
            label="Date Format"
            options={DATE_FORMAT_OPTIONS}
            value={localPrefs.dateFormat || 'MM/DD/YYYY'}
            onChange={value => handleChange('dateFormat', value as string)}
            placeholder="Select date format"
          />

          <Dropdown
            label="Time Format"
            options={TIME_FORMAT_OPTIONS}
            value={localPrefs.timeFormat || '12h'}
            onChange={value => handleChange('timeFormat', value as string)}
            placeholder="Select time format"
          />
        </div>

        {/* Preview */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Clock className="w-4 h-4 text-gray-400" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Preview
            </span>
          </div>
          <p className="text-sm text-gray-700">
            {formatPreview(localPrefs.dateFormat || 'MM/DD/YYYY', localPrefs.timeFormat || '12h')}
          </p>
        </div>
      </div>

      {/* Theme */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 mb-4">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-9 h-9 bg-brand-50 rounded-lg flex items-center justify-center">
            <Palette className="w-4 h-4 text-brand-primary" />
          </div>
          <div>
            <h2 className="text-sm font-medium text-gray-900">Theme</h2>
            <p className="text-xs text-gray-500">Choose your preferred appearance</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {THEME_OPTIONS.map(theme => {
            const IconComponent = theme.icon;
            const isSelected = localPrefs.theme === theme.value;

            return (
              <button
                key={theme.value}
                onClick={() => handleChange('theme', theme.value)}
                className={`p-4 rounded-lg border-2 transition-all text-left ${
                  isSelected
                    ? 'border-brand-primary bg-brand-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-3 mb-2">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isSelected ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    <IconComponent className="w-5 h-5" />
                  </div>
                  <span
                    className={`font-medium ${isSelected ? 'text-brand-primary' : 'text-gray-900'}`}
                  >
                    {theme.label}
                  </span>
                </div>
                <p className="text-xs text-gray-500">{theme.description}</p>
              </button>
            );
          })}
        </div>

        <p className="mt-4 text-xs text-gray-500">
          Note: Dark mode is coming soon. Currently, light mode is used regardless of this setting.
        </p>
      </div>

      {/* Save Button */}
      {hasChanges && (
        <div className="fixed bottom-6 right-6 z-50">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-3 bg-brand-primary text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors shadow-lg disabled:opacity-50 flex items-center space-x-2"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Help Section */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-5">
        <div className="flex items-center space-x-2 mb-3">
          <Globe className="w-4 h-4 text-gray-600" />
          <h3 className="text-sm font-medium text-gray-900">About Preferences</h3>
        </div>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex items-start space-x-2">
            <span className="text-brand-primary mt-1">•</span>
            <span>Language setting affects all text throughout the application.</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-brand-primary mt-1">•</span>
            <span>Timezone affects how dates and times are displayed for your account.</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-brand-primary mt-1">•</span>
            <span>Date and time formats apply to all dates shown in the system.</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

// Helper function to format preview based on selected formats
function formatPreview(dateFormat: string, timeFormat: string): string {
  const now = new Date();
  let dateStr = '';
  let timeStr = '';

  // Format date
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const year = now.getFullYear();
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const monthName = monthNames[now.getMonth()];

  switch (dateFormat) {
    case 'MM/DD/YYYY':
      dateStr = `${month}/${day}/${year}`;
      break;
    case 'DD/MM/YYYY':
      dateStr = `${day}/${month}/${year}`;
      break;
    case 'YYYY-MM-DD':
      dateStr = `${year}-${month}-${day}`;
      break;
    case 'DD MMM YYYY':
      dateStr = `${day} ${monthName} ${year}`;
      break;
    case 'MMM DD, YYYY':
      dateStr = `${monthName} ${day}, ${year}`;
      break;
    default:
      dateStr = `${month}/${day}/${year}`;
  }

  // Format time
  const hours = now.getHours();
  const minutes = String(now.getMinutes()).padStart(2, '0');

  if (timeFormat === '24h') {
    timeStr = `${String(hours).padStart(2, '0')}:${minutes}`;
  } else {
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    timeStr = `${hours12}:${minutes} ${period}`;
  }

  return `${dateStr} at ${timeStr}`;
}

export default ProfilePreferences;
